<template>
	<div class="lp">
		<header
			class="lp-header"
			:class="{ 'lp-header--scrolled': isScrolled }"
		>
			<a
				href="#home"
				class="lp-header__brand"
				@click.prevent="scrollToSection('home')"
				aria-label="MyLegis — início"
			>
				<img
					src="../assets/logo.png"
					alt="MyLegis"
					class="lp-header__logo"
					width="140"
					height="36"
				/>
			</a>

			<nav
				v-if="intBreakPoint >= 2"
				class="lp-header__nav"
				aria-label="Navegação principal"
			>
				<a
					href="#recursos"
					@click.prevent="scrollToSection('recursos')"
					>Recursos</a
				>
				<a
					href="#mevio"
					@click.prevent="scrollToSection('mevio')"
					>Mévio</a
				>
				<a
					href="#planos"
					@click.prevent="scrollToSection('planos')"
					>Planos</a
				>
				<a
					href="#faq"
					@click.prevent="scrollToSection('faq')"
					>FAQ</a
				>
			</nav>

			<div
				v-if="intBreakPoint >= 2"
				class="lp-header__actions"
			>
				<router-link
					to="/login"
					class="lp-btn lp-btn--ghost"
					>Entrar</router-link
				>
				<router-link
					to="/cadastro/plano-free"
					class="lp-btn lp-btn--primary"
					>Começar grátis</router-link
				>
			</div>

			<button
				v-else
				type="button"
				class="lp-header__menu"
				aria-label="Abrir menu"
				@click="drawer = true"
			>
				<v-icon color="#1a1a1a">mdi-menu</v-icon>
			</button>
		</header>

		<v-navigation-drawer
			v-model="drawer"
			temporary
			app
			class="lp-drawer"
		>
			<div class="lp-drawer__inner">
				<img
					src="../assets/logo.png"
					alt="MyLegis"
					width="130"
					height="34"
				/>
				<nav class="lp-drawer__nav">
					<a
						href="#recursos"
						@click.prevent="
							scrollToSection('recursos');
							drawer = false;
						"
						>Recursos</a
					>
					<a
						href="#mevio"
						@click.prevent="
							scrollToSection('mevio');
							drawer = false;
						"
						>Mévio</a
					>
					<a
						href="#planos"
						@click.prevent="
							scrollToSection('planos');
							drawer = false;
						"
						>Planos</a
					>
					<a
						href="#faq"
						@click.prevent="
							scrollToSection('faq');
							drawer = false;
						"
						>FAQ</a
					>
					<a
						href="#contato"
						@click.prevent="
							openContact(2);
							drawer = false;
						"
						>Contato</a
					>
				</nav>
				<router-link
					to="/login"
					class="lp-btn lp-btn--ghost lp-btn--block"
					@click.native="drawer = false"
					>Entrar</router-link
				>
				<router-link
					to="/cadastro/plano-free"
					class="lp-btn lp-btn--primary lp-btn--block"
					@click.native="drawer = false"
					>Começar grátis</router-link
				>
			</div>
		</v-navigation-drawer>

		<main>
			<section
				id="home"
				class="lp-hero"
				aria-labelledby="lp-hero-title"
			>
				<div class="lp-hero__atmosphere" aria-hidden="true"></div>
				<div class="lp-hero__content">
					<p class="lp-brand-mark">MyLegis</p>
					<h1 id="lp-hero-title" class="lp-hero__title">
						Vade-mécum digital com anotações e rede jurídica
					</h1>
					<p class="lp-hero__lead">
						Organize legislações, anote dispositivos, compartilhe com colegas e
						conte com o Mévio, seu assistente de IA no painel.
					</p>
					<div class="lp-hero__cta">
						<router-link
							to="/cadastro/plano-free"
							class="lp-btn lp-btn--primary lp-btn--lg"
						>
							Cadastre-se grátis
						</router-link>
						<a
							href="#recursos"
							class="lp-btn lp-btn--text"
							@click.prevent="scrollToSection('recursos')"
						>
							Ver recursos
						</a>
					</div>
				</div>
				<div class="lp-hero__visual">
					<img
						src="../assets/home/firstimg.svg"
						alt="Interface do MyLegis com vade-mécuns digitais personalizados"
						width="640"
						height="520"
						fetchpriority="high"
					/>
				</div>
			</section>

			<section
				v-if="parceiros && parceiros.length"
				class="lp-section lp-partners"
				aria-labelledby="parceiros-title"
			>
				<div class="lp-container">
					<p id="parceiros-title" class="lp-kicker">Parceiros oficiais</p>
					<div class="lp-partners__track">
						<a
							v-for="b in parceiros"
							:key="b.directus_files_id.id"
							:href="b.link"
							target="_blank"
							rel="noopener noreferrer"
							class="lp-partners__item"
						>
							<img
								:src="`https://api.mylegis.com.br/assets/${b.directus_files_id.id}`"
								:alt="b.directus_files_id.description || 'Parceiro MyLegis'"
								loading="lazy"
							/>
						</a>
					</div>
				</div>
			</section>

			<section
				id="recursos"
				class="lp-section"
				aria-labelledby="recursos-title"
			>
				<div class="lp-container">
					<header class="lp-section__head">
						<p class="lp-kicker">O que você encontra</p>
						<h2 id="recursos-title">
							Tudo o que o MyLegis entrega hoje
						</h2>
						<p class="lp-section__sub">
							Funcionalidades reais da plataforma — sem promessas que não
							existem no produto.
						</p>
					</header>

					<ul class="lp-features">
						<li
							v-for="feature in features"
							:key="feature.title"
							class="lp-feature"
						>
							<span
								class="lp-feature__icon"
								aria-hidden="true"
							>
								<v-icon color="#F15A24">{{ feature.icon }}</v-icon>
							</span>
							<h3>{{ feature.title }}</h3>
							<p>{{ feature.text }}</p>
						</li>
					</ul>
				</div>
			</section>

			<section
				id="beneficios"
				class="lp-section lp-section--muted"
				aria-labelledby="vade-title"
			>
				<div class="lp-container lp-split">
					<div class="lp-split__copy">
						<p class="lp-kicker">Vade-mécum personalizado</p>
						<h2 id="vade-title">
							Monte seus próprios vade-mécuns e consulte a lei no seu ritmo
						</h2>
						<p>
							Crie coleções de legislações, adicione leis e projetos de lei e
							navegue pelos dispositivos com anotações, mídias e links
							vinculados a cada artigo.
						</p>
						<router-link
							to="/cadastro/plano-free"
							class="lp-btn lp-btn--primary"
						>
							Criar meu vade-mécum
						</router-link>
					</div>
					<div class="lp-split__media">
						<img
							src="../assets/home/mycards.svg"
							alt="Exemplos de vade-mécums personalizados no MyLegis"
							loading="lazy"
							width="520"
							height="360"
						/>
					</div>
				</div>
			</section>

			<section
				class="lp-section"
				aria-labelledby="social-title"
			>
				<div class="lp-container lp-split lp-split--reverse">
					<div class="lp-split__copy">
						<p class="lp-kicker">Rede jurídica</p>
						<h2 id="social-title">
							Compartilhe anotações e acompanhe colegas no feed
						</h2>
						<p>
							Publique posts públicos ou entre amigos e grupos, curta e comente
							conteúdos da comunidade e importe anotações de terceiros para os
							seus vade-mécuns.
						</p>
						<button
							type="button"
							class="lp-btn lp-btn--primary"
							@click="openContact(1)"
						>
							Tirar dúvidas
						</button>
					</div>
					<div class="lp-split__media">
						<img
							src="../assets/who/grupoColor.jpg"
							alt="Profissionais do Direito conectados na rede jurídica MyLegis"
							loading="lazy"
							width="520"
							height="360"
							class="lp-split__photo"
						/>
					</div>
				</div>
			</section>

			<section
				id="mevio"
				class="lp-section lp-mevio"
				aria-labelledby="mevio-title"
			>
				<div class="lp-container lp-mevio__grid">
					<div class="lp-mevio__copy">
						<p class="lp-kicker lp-kicker--on-dark">Assistente virtual</p>
						<h2 id="mevio-title">Conheça o Mévio</h2>
						<p>
							No painel logado, o Mévio é o assistente de inteligência
							artificial do MyLegis. Ele ajuda a tirar dúvidas sobre o uso da
							plataforma e a trabalhar com conteúdos jurídicos disponíveis no
							aplicativo — com consentimento explícito antes do primeiro uso.
						</p>
						<ul class="lp-mevio__list">
							<li>Chat disponível dentro do painel</li>
							<li>Apoio a consultas e geração de textos no fluxo do app</li>
							<li>Uso sujeito à política de privacidade e ao aceite de IA</li>
						</ul>
						<router-link
							to="/cadastro/plano-free"
							class="lp-btn lp-btn--light"
						>
							Começar e falar com o Mévio
						</router-link>
					</div>
					<div class="lp-mevio__visual">
						<img
							src="../assets/who/mevioColor.png"
							alt="Mévio, assistente de IA jurídica do MyLegis"
							loading="lazy"
							width="360"
							height="360"
						/>
					</div>
				</div>
			</section>

			<section
				class="lp-section"
				aria-labelledby="publico-title"
			>
				<div class="lp-container">
					<header class="lp-section__head">
						<p class="lp-kicker">Para quem é</p>
						<h2 id="publico-title">
							Feito para quem estuda e aplica o Direito
						</h2>
						<p class="lp-section__sub">
							Estudantes, advogados, professores, concurseiros e equipes que
							precisam organizar legislações e anotações em um só lugar.
						</p>
					</header>
					<ul class="lp-audience">
						<li
							v-for="persona in audience"
							:key="persona.name"
						>
							<img
								:src="persona.image"
								:alt="persona.alt"
								loading="lazy"
								width="120"
								height="120"
							/>
							<strong>{{ persona.name }}</strong>
							<span>{{ persona.role }}</span>
						</li>
					</ul>
				</div>
			</section>

			<section
				v-if="depoimentos.length"
				class="lp-section lp-section--brand"
				aria-labelledby="depoimentos-title"
			>
				<div class="lp-container">
					<header class="lp-section__head lp-section__head--light">
						<p class="lp-kicker lp-kicker--on-dark">Depoimentos</p>
						<h2 id="depoimentos-title">O que estão dizendo</h2>
					</header>
					<div
						class="lp-testimonials"
						role="list"
					>
						<figure
							v-for="item in depoimentos"
							:key="item.alt"
							class="lp-testimonial"
							role="listitem"
						>
							<img
								:src="item.src"
								:alt="item.alt"
								loading="lazy"
							/>
						</figure>
					</div>
				</div>
			</section>

			<section
				id="planos"
				class="lp-section"
				aria-labelledby="planos-title"
			>
				<div class="lp-container">
					<header class="lp-section__head">
						<p class="lp-kicker">Comece agora</p>
						<h2 id="planos-title">Plano gratuito para experimentar</h2>
						<p class="lp-section__sub">
							Crie sua conta, monte vade-mécuns e use a rede jurídica sem
							cartão.
						</p>
					</header>

					<div class="lp-plans">
						<article
							v-for="plan in plans"
							:key="plan.title"
							class="lp-plan"
						>
							<p class="lp-plan__name">{{ plan.title }}</p>
							<p class="lp-plan__price">
								{{ plan.valor }}
								<span v-if="plan.espaco"> · {{ plan.espaco }}</span>
							</p>
							<ul>
								<li
									v-for="detail in plan.detalhes"
									:key="detail"
								>
									{{ detail }}
								</li>
							</ul>
							<router-link
								v-if="!planoContratacaoCtaDesabilitado(plan.route)"
								:to="plan.route"
								class="lp-btn lp-btn--primary lp-btn--block"
							>
								Cadastre-se grátis
							</router-link>
							<button
								v-else
								type="button"
								class="lp-btn lp-btn--primary lp-btn--block"
								disabled
							>
								Em breve
							</button>
						</article>
					</div>
				</div>
			</section>

			<section
				id="faq"
				class="lp-section lp-section--muted"
				aria-labelledby="faq-title"
			>
				<div class="lp-container lp-faq">
					<header class="lp-section__head">
						<p class="lp-kicker">FAQ</p>
						<h2 id="faq-title">Perguntas frequentes</h2>
					</header>

					<div
						v-if="faq.length"
						class="lp-faq__list"
					>
						<details
							v-for="(item, index) in faq"
							:key="item.pergunta || item.funcao || index"
							class="lp-faq__item"
						>
							<summary>{{ stripHtml(item.funcao || item.pergunta) }}</summary>
							<div
								class="lp-faq__body"
								v-html="item.resposta || item.pergunta"
							></div>
							<button
								type="button"
								class="lp-link"
								@click="
									showFaq = index;
									verFaq = true;
								"
							>
								Ver detalhes
							</button>
						</details>
					</div>
					<p
						v-else
						class="lp-section__sub"
					>
						Carregando perguntas…
					</p>
				</div>
			</section>

			<section
				v-if="banners && banners.length"
				class="lp-section"
				aria-label="Patrocínio"
			>
				<div class="lp-container lp-sponsor">
					<p class="lp-kicker">Patrocínio</p>
					<v-carousel
						v-model="showBannerDestaque"
						height="180"
						cycle
						:interval="6000"
						hide-delimiters
						:show-arrows="false"
					>
						<v-carousel-item
							v-for="b in banners"
							:key="b.directus_files_id"
						>
							<a
								:href="b.link"
								target="_blank"
								rel="noopener noreferrer"
							>
								<img
									:src="`https://api.mylegis.com.br/assets/${b.directus_files_id}`"
									alt="Banner de patrocínio MyLegis"
									class="lp-sponsor__img"
									loading="lazy"
								/>
							</a>
						</v-carousel-item>
					</v-carousel>
				</div>
			</section>

			<section
				id="contato"
				class="lp-cta"
				aria-labelledby="cta-title"
			>
				<div class="lp-container lp-cta__inner">
					<h2 id="cta-title">Pronto para organizar seu estudo jurídico?</h2>
					<p>
						Crie sua conta gratuita e comece a montar vade-mécuns, anotações e
						sua rede no MyLegis.
					</p>
					<div class="lp-cta__actions">
						<router-link
							to="/cadastro/plano-free"
							class="lp-btn lp-btn--light lp-btn--lg"
						>
							Cadastre-se grátis
						</router-link>
						<button
							type="button"
							class="lp-btn lp-btn--ghost-light"
							@click="openContact(1)"
						>
							Falar com a gente
						</button>
					</div>
				</div>
			</section>
		</main>

		<footer class="lp-footer">
			<div class="lp-container lp-footer__grid">
				<div>
					<img
						src="../assets/home/myy.svg"
						alt="MyLegis"
						width="120"
						height="40"
					/>
					<p class="lp-footer__about">
						Plataforma de vade-mécum digital, anotações e rede jurídica — com o
						assistente Mévio no painel.
					</p>
					<div class="lp-footer__social">
						<a
							href="https://www.instagram.com/mylegis/"
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Instagram MyLegis"
						>
							<img
								src="../assets/home/linkinsta.svg"
								alt=""
								width="28"
								height="28"
							/>
						</a>
						<a
							href="https://www.facebook.com/people/Mylegis/61556276132184/"
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Facebook MyLegis"
						>
							<img
								src="../assets/home/linkface.svg"
								alt=""
								width="28"
								height="28"
							/>
						</a>
					</div>
					<a href="mailto:administrativo@mylegis.com.br"
						>administrativo@mylegis.com.br</a
					>
				</div>

				<div>
					<p class="lp-footer__title">Navegação</p>
					<button type="button" @click="scrollToSection('home')">Início</button>
					<button type="button" @click="scrollToSection('recursos')">Recursos</button>
					<button type="button" @click="scrollToSection('planos')">Planos</button>
					<button type="button" @click="scrollToSection('faq')">FAQ</button>
					<button type="button" @click="openContact(2)">Contato</button>
					<button type="button" @click="openContact(0)">Sugerir legislação</button>
				</div>

				<div>
					<p class="lp-footer__title">Legal</p>
					<router-link to="/politica-de-privacidade"
						>Política de privacidade</router-link
					>
					<button
						type="button"
						@click="scrollToSection('faq')"
					>
						Perguntas frequentes
					</button>
				</div>
			</div>
			<p class="lp-footer__copy">
				© {{ currentYear }} MyLegis. Todos os direitos reservados.
			</p>
		</footer>

		<v-dialog
			v-model="verFaq"
			width="700"
		>
			<v-card
				v-if="showFaq != null && faq[showFaq]"
				class="lp-dialog pa-6"
			>
				<p class="lp-dialog__title">{{ faq[showFaq].funcao }}</p>
				<iframe
					v-if="faq[showFaq].link"
					width="100%"
					height="350"
					:src="faq[showFaq].link"
					:title="faq[showFaq].funcao"
					frameborder="0"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					allowfullscreen
				></iframe>
				<div
					class="lp-dialog__body"
					v-html="faq[showFaq].resposta"
				></div>
				<div class="lp-dialog__actions">
					<button
						type="button"
						class="lp-link"
						@click="
							verContato = true;
							typeSelected = 1;
						"
					>
						Ainda em dúvidas? Entre em contato
					</button>
					<button
						type="button"
						class="lp-btn lp-btn--primary"
						@click="
							scrollToSection('planos');
							verFaq = false;
						"
					>
						Cadastre-se grátis
					</button>
				</div>
			</v-card>
		</v-dialog>

		<v-dialog
			v-model="verContato"
			width="700"
		>
			<v-card class="lp-dialog pa-4">
				<v-card-title class="d-flex justify-space-between align-center">
					<span>
						{{
							contactTypes[typeSelected].tipo.charAt(0).toUpperCase() +
							contactTypes[typeSelected].tipo.slice(1)
						}}
					</span>
					<v-btn
						icon
						aria-label="Fechar"
						@click="verContato = false"
					>
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<v-text-field
						prepend-inner-icon="mdi-account"
						color="#F15A24"
						filled
						v-model="contato.nome"
						hide-details
						label="Nome"
						class="mb-3"
					/>
					<v-text-field
						prepend-inner-icon="mdi-email"
						color="#F15A24"
						filled
						v-model="contato.email"
						hide-details
						label="Email"
						class="mb-3"
					/>
					<v-textarea
						clearable
						filled
						v-model="contato.mensagem"
						color="#F15A24"
						:label="contactTypes[typeSelected].title"
					/>
				</v-card-text>
				<v-card-actions class="justify-center pb-4">
					<v-btn
						color="#F15A24"
						dark
						@click="sendSugestion"
					>
						Enviar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
	import { CONTRATACAO_PLANOS_PAGOS_DESABILITADA } from '../config/contratacaoPlanos.js';
	import { mustUseStoreBilling } from '../services/billingService';

	const SEO = {
		title:
			'MyLegis | Vade-mécum digital, anotações e rede jurídica com IA Mévio',
		description:
			'Organize vade-mécuns personalizados, anote dispositivos legais, compartilhe na rede jurídica e use o Mévio, assistente de IA do MyLegis. Cadastre-se grátis.',
		url: 'https://www.mylegis.com.br/',
		image: 'https://www.mylegis.com.br/img/icons/android-chrome-512x512.png',
	};

	export default {
		name: 'home',
		data() {
			return {
				banners: [],
				showBannerDestaque: 0,
				drawer: false,
				isScrolled: false,
				verFaq: false,
				verContato: false,
				contato: { nome: '', email: '', mensagem: '' },
				showFaq: null,
				faq: [],
				typeSelected: 0,
				contactTypes: [
					{
						tipo: 'sugerir',
						title: 'Indique a legislação que gostaria de ver na MyLegis',
					},
					{ tipo: 'dúvida', title: 'Escreva aqui a sua dúvida ou sugestão' },
					{ tipo: 'contato', title: 'Escreva aqui a sua mensagem ou sugestão' },
				],
				parceiros: [],
				contratacaoPlanosPagosDesabilitada: CONTRATACAO_PLANOS_PAGOS_DESABILITADA,
				features: [
					{
						icon: 'mdi-book-open-page-variant',
						title: 'Vade-mécum digital',
						text: 'Crie e personalize coleções de legislações para consulta rápida.',
					},
					{
						icon: 'mdi-note-text-outline',
						title: 'Anotações nos dispositivos',
						text: 'Inclua comentários, mídias e links diretamente em cada artigo.',
					},
					{
						icon: 'mdi-account-group-outline',
						title: 'Rede jurídica',
						text: 'Publique, curta, comente e importe posts de amigos e grupos.',
					},
					{
						icon: 'mdi-folder-multiple-outline',
						title: 'Grupos e colaboração',
						text: 'Participe de grupos, compartilhe arquivos e vade-mécums em equipe.',
					},
					{
						icon: 'mdi-file-document-outline',
						title: 'Documentos',
						text: 'Organize seus arquivos no mesmo ambiente de estudo e trabalho.',
					},
					{
						icon: 'mdi-robot-outline',
						title: 'Mévio (IA)',
						text: 'Assistente virtual no painel para apoiar dúvidas e fluxos do app.',
					},
				],
				audience: [
					{
						name: 'Themis',
						role: 'Magistratura e estudo aprofundado',
						image: require('../assets/who/themisColor.png'),
						alt: 'Persona Themis — juíza',
					},
					{
						name: 'Advogados',
						role: 'Consulta e organização diária',
						image: require('../assets/who/caioColor.png'),
						alt: 'Persona representante de advogados',
					},
					{
						name: 'Estudantes',
						role: 'Graduação e pós em Direito',
						image: require('../assets/who/maria.png'),
						alt: 'Persona estudante de Direito',
					},
					{
						name: 'Concurseiros',
						role: 'Preparação com lei seca organizada',
						image: require('../assets/who/sicranoColor.png'),
						alt: 'Persona concurseiro',
					},
					{
						name: 'Professores',
						role: 'Ensino e materiais de aula',
						image: require('../assets/who/ticio.png'),
						alt: 'Persona professor de Direito',
					},
				],
				plans: [
					{
						title: 'Plano Iniciante',
						valor: 'Grátis',
						espaco: '10MB',
						route: '/cadastro/plano-free',
						detalhes: [
							'Crie vade-mécuns personalizados',
							'Adicione comentários e mídias em dispositivos legais',
							'Publique posts entre amigos e grupos',
							'Curta e comente posts na rede jurídica',
							'Participe de grupos',
							'Importe posts de terceiros para seus vade-mécuns',
							'Pesquise termos nas legislações do vade-mécum',
							'Organize seus documentos',
							'Acesse o assistente Mévio no painel',
						],
					},
				],
				depoimentos: [
					{ src: require('../assets/home/d1.webp'), alt: 'Depoimento de usuário MyLegis 1' },
					{ src: require('../assets/home/d2.webp'), alt: 'Depoimento de usuário MyLegis 2' },
					{ src: require('../assets/home/d3.webp'), alt: 'Depoimento de usuário MyLegis 3' },
					{ src: require('../assets/home/d4.webp'), alt: 'Depoimento de usuário MyLegis 4' },
					{ src: require('../assets/home/d5.webp'), alt: 'Depoimento de usuário MyLegis 5' },
					{ src: require('../assets/home/d6.webp'), alt: 'Depoimento de usuário MyLegis 6' },
				],
				seoNodes: [],
			};
		},
		computed: {
			currentYear() {
				return new Date().getFullYear();
			},
			intBreakPoint() {
				switch (this.$vuetify.breakpoint.name) {
					case 'xs':
						return 0;
					case 'sm':
						return 1;
					case 'md':
						return 2;
					case 'lg':
						return 3;
					case 'xl':
						return 4;
					default:
						return 0;
				}
			},
		},
		mounted() {
			this.applySeo();
			window.addEventListener('scroll', this.handleScroll, { passive: true });
			this.reloadFaq();
			this.reloadBanners();
			this.reloadParceiros();
		},
		beforeDestroy() {
			window.removeEventListener('scroll', this.handleScroll);
			this.removeSeo();
		},
		methods: {
			planoContratacaoCtaDesabilitado(route) {
				// App nativo: nunca direciona para fluxo de cartão web
				if (mustUseStoreBilling()) {
					if (!route || route === '/cadastro/plano-free') return false;
					return true;
				}
				if (!this.contratacaoPlanosPagosDesabilitada) return false;
				if (!route || route === '/cadastro/plano-free') return false;
				return true;
			},
			openContact(typeIndex) {
				this.typeSelected = typeIndex;
				this.verContato = true;
			},
			stripHtml(value) {
				if (!value) return '';
				return String(value).replace(/<[^>]*>/g, '');
			},
			upsertMeta(attr, key, content) {
				if (!content) return;
				let el = document.head.querySelector(`meta[${attr}="${key}"]`);
				if (!el) {
					el = document.createElement('meta');
					el.setAttribute(attr, key);
					document.head.appendChild(el);
					this.seoNodes.push(el);
				}
				el.setAttribute('content', content);
			},
			upsertLink(rel, href) {
				let el = document.head.querySelector(`link[rel="${rel}"]`);
				if (!el) {
					el = document.createElement('link');
					el.setAttribute('rel', rel);
					document.head.appendChild(el);
					this.seoNodes.push(el);
				}
				el.setAttribute('href', href);
			},
			upsertJsonLd(id, data) {
				let el = document.getElementById(id);
				if (!el) {
					el = document.createElement('script');
					el.type = 'application/ld+json';
					el.id = id;
					document.head.appendChild(el);
					this.seoNodes.push(el);
				}
				el.textContent = JSON.stringify(data);
			},
			applySeo() {
				document.title = SEO.title;
				this.upsertMeta('name', 'description', SEO.description);
				this.upsertMeta(
					'name',
					'keywords',
					'vade-mécum digital, anotações jurídicas, rede social jurídica, legislação, MyLegis, Mévio, assistente IA jurídica'
				);
				this.upsertMeta('name', 'robots', 'index, follow, max-image-preview:large');
				this.upsertMeta('name', 'author', 'MyLegis');
				this.upsertMeta('property', 'og:type', 'website');
				this.upsertMeta('property', 'og:locale', 'pt_BR');
				this.upsertMeta('property', 'og:site_name', 'MyLegis');
				this.upsertMeta('property', 'og:title', SEO.title);
				this.upsertMeta('property', 'og:description', SEO.description);
				this.upsertMeta('property', 'og:url', SEO.url);
				this.upsertMeta('property', 'og:image', SEO.image);
				this.upsertMeta('name', 'twitter:card', 'summary_large_image');
				this.upsertMeta('name', 'twitter:title', SEO.title);
				this.upsertMeta('name', 'twitter:description', SEO.description);
				this.upsertMeta('name', 'twitter:image', SEO.image);
				this.upsertLink('canonical', SEO.url);

				this.upsertJsonLd('lp-schema-org', {
					'@context': 'https://schema.org',
					'@type': 'Organization',
					name: 'MyLegis',
					url: SEO.url,
					logo: SEO.image,
					email: 'administrativo@mylegis.com.br',
					sameAs: [
						'https://www.instagram.com/mylegis/',
						'https://www.facebook.com/people/Mylegis/61556276132184/',
					],
				});

				this.upsertJsonLd('lp-schema-app', {
					'@context': 'https://schema.org',
					'@type': 'SoftwareApplication',
					name: 'MyLegis',
					applicationCategory: 'BusinessApplication',
					operatingSystem: 'Web, iOS, Android',
					description: SEO.description,
					url: SEO.url,
					offers: {
						'@type': 'Offer',
						price: '0',
						priceCurrency: 'BRL',
					},
					featureList: [
						'Vade-mécum digital personalizado',
						'Anotações em dispositivos legais',
						'Rede jurídica com feed, amigos e grupos',
						'Organização de documentos',
						'Assistente de IA Mévio no painel',
					],
				});
			},
			applyFaqSchema() {
				if (!this.faq.length) return;
				const mainEntity = this.faq
					.filter((item) => item.funcao || item.pergunta)
					.map((item) => ({
						'@type': 'Question',
						name: this.stripHtml(item.funcao || item.pergunta),
						acceptedAnswer: {
							'@type': 'Answer',
							text: this.stripHtml(item.resposta || item.pergunta || ''),
						},
					}));
				if (!mainEntity.length) return;
				this.upsertJsonLd('lp-schema-faq', {
					'@context': 'https://schema.org',
					'@type': 'FAQPage',
					mainEntity,
				});
			},
			removeSeo() {
				this.seoNodes.forEach((node) => {
					if (node && node.parentNode) node.parentNode.removeChild(node);
				});
				this.seoNodes = [];
			},
			async reloadFaq() {
				try {
					const { data } = await this.$http.get(
						`items/faq/?filter[pagina][_eq]=home&filter[status][_eq]=published`
					);
					this.faq = data.data || [];
					this.applyFaqSchema();
				} catch (error) {
					window.console.warn('Não foi possível carregar o FAQ:', error);
				}
			},
			async reloadBanners() {
				try {
					const { data } = await this.$http.get(
						`/items/banners/?fields=*,banners.*&filter[local][_eq]=home-destaque`
					);
					this.banners =
						data.data && data.data[0] && data.data[0].banners
							? data.data[0].banners
							: [];
				} catch (error) {
					window.console.warn('Não foi possível carregar banners:', error);
				}
			},
			async reloadParceiros() {
				try {
					const { data } = await this.$http.get(
						`/items/banners/?fields=*,banners.directus_files_id.*&filter[local][_eq]=parceiros`
					);
					this.parceiros =
						data.data && data.data[0] && data.data[0].banners
							? data.data[0].banners
							: [];
				} catch (error) {
					window.console.warn('Não foi possível carregar parceiros:', error);
				}
			},
			handleScroll() {
				this.isScrolled = window.scrollY > 8;
			},
			scrollToSection(sectionId) {
				const section = document.getElementById(sectionId);
				if (section) {
					section.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			},
			sendSugestion() {
				this.$http
					.post('/items/contato', {
						nome: this.contato.nome,
						email: this.contato.email,
						mensagem: this.contato.mensagem,
						tipo: this.contactTypes[this.typeSelected].tipo,
					})
					.then(() => {
						this.contato = { nome: '', email: '', mensagem: '' };
						this.verContato = false;
						alert(
							'Obrigado por nos enviar seu contato, retornaremos o mais breve possível.'
						);
					});
			},
		},
	};
</script>

<style scoped>
	.lp {
		--lp-brand: #f15a24;
		--lp-brand-dark: #d94a18;
		--lp-ink: #161616;
		--lp-muted: #5c5c5c;
		--lp-bg: #f7f4f1;
		--lp-surface: #ffffff;
		--lp-line: rgba(22, 22, 22, 0.08);
		--lp-radius: 18px;
		--lp-font-display: 'Outfit', 'Segoe UI', sans-serif;
		--lp-font-body: 'Source Sans 3', 'Segoe UI', sans-serif;
		color: var(--lp-ink);
		background: var(--lp-surface);
		font-family: var(--lp-font-body) !important;
		overflow-x: hidden;
	}

	.lp,
	.lp ::v-deep *:not(.v-icon):not(.mdi):not([class*='mdi-']) {
		font-family: var(--lp-font-body) !important;
	}

	.lp h1,
	.lp h2,
	.lp h3,
	.lp .lp-brand-mark,
	.lp .lp-btn,
	.lp .lp-plan__price,
	.lp .lp-dialog__title,
	.lp .lp-footer__title,
	.lp .lp-feature h3,
	.lp .lp-audience strong,
	.lp .lp-faq__item summary {
		font-family: var(--lp-font-display) !important;
	}

	.lp * {
		box-sizing: border-box;
	}

	.lp-container {
		width: min(1120px, calc(100% - 2.5rem));
		margin: 0 auto;
	}

	.lp-header {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		z-index: 40;
		display: flex;
		align-items: center;
		gap: 1.5rem;
		padding: 0.9rem clamp(1rem, 4vw, 2.5rem);
		background: rgba(255, 255, 255, 0.86);
		backdrop-filter: blur(12px);
		border-bottom: 1px solid transparent;
		transition: border-color 0.25s ease, box-shadow 0.25s ease;
	}

	.lp-header--scrolled {
		border-bottom-color: var(--lp-line);
		box-shadow: 0 8px 24px rgba(22, 22, 22, 0.04);
	}

	.lp-header__brand {
		display: inline-flex;
		align-items: center;
	}

	.lp-header__logo {
		display: block;
		height: 36px;
		width: auto;
	}

	.lp-header__nav {
		display: flex;
		gap: 1.25rem;
		margin-left: auto;
	}

	.lp-header__nav a {
		color: var(--lp-ink) !important;
		text-decoration: none !important;
		font-size: 0.95rem;
		font-weight: 600;
	}

	.lp-header__actions {
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.lp-header__menu {
		margin-left: auto;
		border: 0;
		background: transparent;
		cursor: pointer;
	}

	.lp-drawer__inner {
		padding: 1.5rem;
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.lp-drawer__nav {
		display: flex;
		flex-direction: column;
		gap: 0.85rem;
		margin: 1rem 0;
	}

	.lp-drawer__nav a {
		color: var(--lp-ink) !important;
		text-decoration: none !important;
		font-weight: 600;
	}

	.lp-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 0.4rem;
		min-height: 44px;
		padding: 0.7rem 1.25rem;
		border-radius: 12px;
		border: 1px solid transparent;
		font-family: var(--lp-font-display);
		font-weight: 700;
		font-size: 0.95rem;
		text-decoration: none !important;
		cursor: pointer;
		transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease;
	}

	.lp-btn:hover {
		transform: translateY(-1px);
	}

	.lp-btn--primary {
		background: var(--lp-brand);
		color: #fff !important;
	}

	.lp-btn--primary:hover {
		background: var(--lp-brand-dark);
	}

	.lp-btn--ghost {
		background: transparent;
		border-color: var(--lp-line);
		color: var(--lp-ink) !important;
	}

	.lp-btn--text {
		background: transparent;
		color: var(--lp-ink) !important;
	}

	.lp-btn--light {
		background: #fff;
		color: var(--lp-brand) !important;
	}

	.lp-btn--ghost-light {
		background: transparent;
		border-color: rgba(255, 255, 255, 0.35);
		color: #fff !important;
	}

	.lp-btn--lg {
		min-height: 52px;
		padding: 0.85rem 1.5rem;
		font-size: 1rem;
	}

	.lp-btn--block {
		width: 100%;
	}

	.lp-btn:disabled {
		opacity: 0.55;
		cursor: not-allowed;
		transform: none;
	}

	.lp-link {
		border: 0;
		background: transparent;
		color: var(--lp-brand) !important;
		font-weight: 700;
		cursor: pointer;
		padding: 0;
		text-decoration: none !important;
	}

	.lp-hero {
		position: relative;
		min-height: 100vh;
		display: grid;
		grid-template-columns: 1.05fr 0.95fr;
		align-items: center;
		gap: 2rem;
		padding: 7rem clamp(1rem, 4vw, 2.5rem) 4rem;
		overflow: hidden;
	}

	.lp-hero__atmosphere {
		position: absolute;
		inset: 0;
		background:
			radial-gradient(ellipse 70% 60% at 85% 20%, rgba(241, 90, 36, 0.22), transparent 55%),
			radial-gradient(ellipse 50% 45% at 10% 80%, rgba(241, 90, 36, 0.1), transparent 50%),
			linear-gradient(180deg, #fff7f2 0%, #ffffff 55%, #f7f4f1 100%);
		z-index: 0;
	}

	.lp-hero__content,
	.lp-hero__visual {
		position: relative;
		z-index: 1;
	}

	.lp-brand-mark {
		font-family: var(--lp-font-display);
		font-size: clamp(2rem, 5vw, 3.4rem);
		font-weight: 800;
		letter-spacing: -0.04em;
		color: var(--lp-brand);
		margin: 0 0 0.75rem;
		line-height: 1;
	}

	.lp-hero__title {
		font-family: var(--lp-font-display);
		font-size: clamp(1.75rem, 3.6vw, 2.75rem);
		font-weight: 700;
		letter-spacing: -0.03em;
		line-height: 1.15;
		margin: 0 0 1rem;
		max-width: 16ch;
	}

	.lp-hero__lead {
		font-size: 1.125rem;
		line-height: 1.55;
		color: var(--lp-muted);
		max-width: 38ch;
		margin: 0 0 1.75rem;
	}

	.lp-hero__cta {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		align-items: center;
	}

	.lp-hero__visual img {
		width: 100%;
		max-width: 640px;
		height: auto;
		display: block;
		margin-left: auto;
		animation: lp-float 7s ease-in-out infinite;
	}

	@keyframes lp-float {
		0%,
		100% {
			transform: translateY(0);
		}
		50% {
			transform: translateY(-10px);
		}
	}

	.lp-section {
		padding: 5rem 0;
	}

	.lp-section--muted {
		background: var(--lp-bg);
	}

	.lp-section--brand {
		background: var(--lp-brand);
		color: #fff;
	}

	.lp-section__head {
		max-width: 640px;
		margin: 0 auto 2.5rem;
		text-align: center;
	}

	.lp-section__head--light h2,
	.lp-section__head--light .lp-section__sub {
		color: #fff;
	}

	.lp-section__head h2,
	.lp-split__copy h2,
	.lp-mevio__copy h2,
	.lp-cta h2 {
		font-family: var(--lp-font-display);
		font-size: clamp(1.6rem, 3vw, 2.35rem);
		font-weight: 750;
		letter-spacing: -0.03em;
		line-height: 1.2;
		margin: 0.35rem 0 0.85rem;
	}

	.lp-section__sub {
		color: var(--lp-muted);
		font-size: 1.05rem;
		line-height: 1.5;
		margin: 0;
	}

	.lp-kicker {
		margin: 0;
		font-size: 0.8rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--lp-brand);
	}

	.lp-kicker--on-dark {
		color: rgba(255, 255, 255, 0.85);
	}

	.lp-partners__track {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		align-items: center;
		gap: 2rem;
	}

	.lp-partners__item img {
		max-height: 64px;
		width: auto;
		max-width: 180px;
		object-fit: contain;
		filter: grayscale(1);
		opacity: 0.75;
		transition: filter 0.2s ease, opacity 0.2s ease;
	}

	.lp-partners__item:hover img {
		filter: none;
		opacity: 1;
	}

	.lp-features {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 1.25rem;
	}

	.lp-feature {
		padding: 1.35rem 1.25rem;
		border: 1px solid var(--lp-line);
		border-radius: var(--lp-radius);
		background: var(--lp-surface);
		transition: border-color 0.2s ease, transform 0.2s ease;
	}

	.lp-feature:hover {
		border-color: rgba(241, 90, 36, 0.35);
		transform: translateY(-3px);
	}

	.lp-feature__icon {
		display: inline-flex;
		margin-bottom: 0.75rem;
	}

	.lp-feature h3 {
		font-family: var(--lp-font-display);
		font-size: 1.1rem;
		margin: 0 0 0.45rem;
	}

	.lp-feature p {
		margin: 0;
		color: var(--lp-muted);
		line-height: 1.45;
		font-size: 0.98rem;
	}

	.lp-split {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 3rem;
		align-items: center;
	}

	.lp-split--reverse .lp-split__copy {
		order: 2;
	}

	.lp-split--reverse .lp-split__media {
		order: 1;
	}

	.lp-split__copy p {
		color: var(--lp-muted);
		line-height: 1.55;
		margin: 0 0 1.25rem;
	}

	.lp-split__media img,
	.lp-split__photo {
		width: 100%;
		height: auto;
		border-radius: var(--lp-radius);
		display: block;
	}

	.lp-mevio {
		background: linear-gradient(135deg, #1c1917 0%, #2a211c 55%, #3b271c 100%);
		color: #fff;
	}

	.lp-mevio__grid {
		display: grid;
		grid-template-columns: 1.2fr 0.8fr;
		gap: 2rem;
		align-items: center;
	}

	.lp-mevio__copy p {
		color: rgba(255, 255, 255, 0.82);
		line-height: 1.55;
		margin: 0 0 1rem;
	}

	.lp-mevio__list {
		margin: 0 0 1.5rem;
		padding-left: 1.1rem;
		color: rgba(255, 255, 255, 0.88);
		line-height: 1.7;
	}

	.lp-mevio__visual img {
		width: min(100%, 360px);
		height: auto;
		display: block;
		margin: 0 auto;
		animation: lp-float 8s ease-in-out infinite;
	}

	.lp-audience {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		grid-template-columns: repeat(5, minmax(0, 1fr));
		gap: 1rem;
		text-align: center;
	}

	.lp-audience img {
		width: 96px;
		height: 96px;
		object-fit: cover;
		border-radius: 50%;
		margin-bottom: 0.75rem;
	}

	.lp-audience strong {
		display: block;
		font-family: var(--lp-font-display);
	}

	.lp-audience span {
		color: var(--lp-muted);
		font-size: 0.9rem;
	}

	.lp-testimonials {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 1rem;
	}

	.lp-testimonial {
		margin: 0;
		border-radius: 14px;
		overflow: hidden;
		background: rgba(255, 255, 255, 0.12);
	}

	.lp-testimonial img {
		width: 100%;
		height: auto;
		display: block;
	}

	.lp-plans {
		display: flex;
		justify-content: center;
	}

	.lp-plan {
		width: min(100%, 420px);
		padding: 1.75rem;
		border: 1px solid var(--lp-line);
		border-radius: var(--lp-radius);
		background: var(--lp-surface);
		box-shadow: 0 18px 40px rgba(22, 22, 22, 0.05);
	}

	.lp-plan__name {
		margin: 0;
		font-weight: 700;
		color: var(--lp-brand);
	}

	.lp-plan__price {
		font-family: var(--lp-font-display);
		font-size: 2rem;
		font-weight: 800;
		margin: 0.35rem 0 1rem;
	}

	.lp-plan ul {
		margin: 0 0 1.5rem;
		padding-left: 1.1rem;
		color: var(--lp-muted);
		line-height: 1.65;
	}

	.lp-faq__list {
		max-width: 760px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.lp-faq__item {
		background: var(--lp-surface);
		border: 1px solid var(--lp-line);
		border-radius: 14px;
		padding: 0.85rem 1rem;
	}

	.lp-faq__item summary {
		cursor: pointer;
		font-family: var(--lp-font-display);
		font-weight: 700;
		list-style: none;
	}

	.lp-faq__item summary::-webkit-details-marker {
		display: none;
	}

	.lp-faq__body {
		margin-top: 0.75rem;
		color: var(--lp-muted);
		line-height: 1.5;
	}

	.lp-sponsor__img {
		max-height: 160px;
		width: auto;
		max-width: 100%;
		display: block;
		margin: 0 auto;
		object-fit: contain;
	}

	.lp-cta {
		padding: 4.5rem 0;
		background:
			radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.12), transparent 40%),
			linear-gradient(135deg, var(--lp-brand), #c2410c);
		color: #fff;
	}

	.lp-cta__inner {
		text-align: center;
		max-width: 680px;
	}

	.lp-cta p {
		margin: 0 0 1.5rem;
		color: rgba(255, 255, 255, 0.9);
		font-size: 1.08rem;
	}

	.lp-cta__actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.75rem;
	}

	.lp-footer {
		background: #111;
		color: rgba(255, 255, 255, 0.78);
		padding: 3.5rem 0 1.5rem;
	}

	.lp-footer a,
	.lp-footer button {
		color: rgba(255, 255, 255, 0.78) !important;
		text-decoration: none !important;
		background: transparent;
		border: 0;
		padding: 0;
		display: block;
		margin-bottom: 0.55rem;
		cursor: pointer;
		text-align: left;
		font: inherit;
	}

	.lp-footer a:hover,
	.lp-footer button:hover {
		color: #fff !important;
	}

	.lp-footer__grid {
		display: grid;
		grid-template-columns: 1.4fr 1fr 1fr;
		gap: 2rem;
		margin-bottom: 2rem;
	}

	.lp-footer__about {
		margin: 1rem 0;
		max-width: 36ch;
		line-height: 1.5;
	}

	.lp-footer__social {
		display: flex;
		gap: 0.75rem;
		margin-bottom: 1rem;
	}

	.lp-footer__title {
		color: #fff;
		font-family: var(--lp-font-display);
		font-weight: 700;
		margin: 0 0 0.85rem;
	}

	.lp-footer__copy {
		width: min(1120px, calc(100% - 2.5rem));
		margin: 0 auto;
		padding-top: 1.25rem;
		border-top: 1px solid rgba(255, 255, 255, 0.1);
		font-size: 0.85rem;
	}

	.lp-dialog__title {
		font-family: var(--lp-font-display);
		font-size: 1.5rem;
		font-weight: 800;
		margin: 0 0 1rem;
	}

	.lp-dialog__actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 1rem;
		flex-wrap: wrap;
		margin-top: 1.5rem;
	}

	@media (max-width: 960px) {
		.lp-hero {
			grid-template-columns: 1fr;
			min-height: auto;
			padding-top: 6rem;
			text-align: left;
		}

		.lp-hero__title {
			max-width: none;
		}

		.lp-hero__visual img {
			margin: 0 auto;
			max-width: 420px;
		}

		.lp-features,
		.lp-audience,
		.lp-testimonials {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.lp-split,
		.lp-mevio__grid,
		.lp-footer__grid {
			grid-template-columns: 1fr;
		}

		.lp-split--reverse .lp-split__copy,
		.lp-split--reverse .lp-split__media {
			order: initial;
		}
	}

	@media (max-width: 640px) {
		.lp-features,
		.lp-audience,
		.lp-testimonials {
			grid-template-columns: 1fr;
		}

		.lp-hero__cta,
		.lp-cta__actions {
			flex-direction: column;
			align-items: stretch;
		}
	}
</style>
